import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AuthImage } from "@/components/AuthImage";
import {
  ArrowLeft, Upload, Search, Trash2, FileText, Download, Loader2,
  HardDrive, Image as ImageIcon, Copy, Archive,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  library: "Library",
  content_image: "Notice images",
  player_photo: "Player photos",
  session_photo: "Session photos",
  payment_proof: "Payment proofs",
  medical_certificate: "Medical certificates",
  membership_card: "Membership cards",
};

const prettyBytes = (b) => {
  if (!b) return "0 KB";
  if (b < 1024 * 1024) return `${Math.max(1, Math.round(b / 1024))} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

export default function AdminMediaLibrary() {
  const [files, setFiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [exportJob, setExportJob] = useState(null);
  const [exporting, setExporting] = useState(false);
  const inputRef = useRef(null);

  const pollExport = useCallback(async (jobId) => {
    for (let i = 0; i < 150; i += 1) {
      try {
        const res = await axios.get(`${API}/media/export/${jobId}`);
        setExportJob(res.data);
        if (res.data.status !== "running") return res.data;
      } catch (err) {
        return null;
      }
      await new Promise((r) => setTimeout(r, 2000));
    }
    return null;
  }, []);

  const startExport = async () => {
    setExporting(true);
    setExportJob(null);
    try {
      const res = await axios.post(`${API}/media/export`);
      const done = await pollExport(res.data.job_id);
      if (done?.status === "ready") {
        toast.success(`Export ready — ${done.files_done} file(s)`);
      } else if (done?.status === "failed") {
        toast.error(done.error || "Export failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start the export");
    } finally {
      setExporting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API}/media`, {
          params: { category: category === "all" ? "" : category, q: query, limit: 60 },
        }),
        axios.get(`${API}/media/stats`),
      ]);
      setFiles(listRes.data.files || []);
      setTotal(listRes.data.total || 0);
      setStats(statsRes.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load the media library");
    } finally {
      setLoading(false);
    }
  }, [category, query]);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async (fileList) => {
    if (!fileList?.length) return;
    const form = new FormData();
    Array.from(fileList).forEach((f) => form.append("files", f));
    form.append("category", category === "all" ? "library" : category);
    setUploading(true);
    try {
      const res = await axios.post(`${API}/media/upload`, form);
      toast.success(`${res.data.files?.length || 0} file(s) uploaded`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/media/${id}`);
      setFiles((prev) => prev.filter((f) => f.id !== id));
      setPreview(null);
      toast.success("File removed from the library");
    } catch (err) {
      toast.error("Could not remove the file");
    }
  };

  const copyReference = async (file) => {
    const ref = file.category === "content_image" ? `[IMG:${file.id}]` : `${API}/media/${file.id}/raw`;
    try {
      await navigator.clipboard.writeText(ref);
      toast.success("Reference copied");
    } catch {
      toast.message(ref);
    }
  };

  const isImage = (f) => (f.content_type || "").startsWith("image/");

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="admin-media-library">
      <div className="max-w-6xl mx-auto px-5 py-8 space-y-7">
        <div className="flex items-center gap-3">
          <Link to="/admin" data-testid="media-back-link">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-1" /> Admin
            </Button>
          </Link>
        </div>

        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-cyan-400/80">Football And More</p>
            <h1 className="text-3xl sm:text-4xl font-bold mt-1">Media library</h1>
            <p className="text-sm text-slate-400 mt-2">
              Every uploaded image and document, stored outside the database.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <input
              ref={inputRef}
              type="file"
              multiple
              className="hidden"
              accept="image/*,application/pdf,.doc,.docx"
              onChange={(e) => upload(e.target.files)}
              data-testid="media-file-input"
            />
            <Button
              variant="outline"
              onClick={startExport}
              disabled={exporting}
              data-testid="media-export-btn"
            >
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Archive className="w-4 h-4 mr-2" />}
              Export all
            </Button>
            <Button
              className="bg-cyan-600 hover:bg-cyan-500"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid="media-upload-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload files
            </Button>
          </div>
        </header>

        {exportJob && (
          <Card className="bg-slate-900/60 border-cyan-500/30" data-testid="media-export-panel">
            <CardContent className="pt-5 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-sm text-white font-medium">
                  {exportJob.status === "running" && "Packing your media…"}
                  {exportJob.status === "ready" && "Your media export is ready"}
                  {exportJob.status === "failed" && "Export failed"}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  {exportJob.status === "failed"
                    ? exportJob.error
                    : `${exportJob.files_done || 0} of ${exportJob.files_total || 0} files · ${prettyBytes(exportJob.bytes)}`}
                  {exportJob.files_failed > 0 ? ` · ${exportJob.files_failed} could not be read` : ""}
                </p>
                <p className="text-[11px] text-slate-500 mt-1">
                  Includes a manifest.csv mapping every file back to its database record. Links expire after 24 hours.
                </p>
              </div>
              {exportJob.status === "ready" && (
                <a
                  href={`${API}/media/export/${exportJob.id}/download?auth=${encodeURIComponent(localStorage.getItem("scc_session_token") || "")}`}
                  data-testid="media-export-download-link"
                >
                  <Button className="bg-emerald-600 hover:bg-emerald-500" data-testid="media-export-download-btn">
                    <Download className="w-4 h-4 mr-2" /> Download ZIP ({prettyBytes(exportJob.zip_bytes)})
                  </Button>
                </a>
              )}
              {exportJob.status === "running" && (
                <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />
              )}
            </CardContent>
          </Card>
        )}

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="media-stats">
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="pt-5">
                <p className="text-xs text-slate-400 flex items-center gap-1">
                  <HardDrive className="w-3.5 h-3.5" /> Stored
                </p>
                <p className="text-2xl font-bold mt-1">{prettyBytes(stats.total_bytes)}</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="pt-5">
                <p className="text-xs text-slate-400 flex items-center gap-1">
                  <ImageIcon className="w-3.5 h-3.5" /> Files
                </p>
                <p className="text-2xl font-bold mt-1">{stats.total_count}</p>
              </CardContent>
            </Card>
            {(stats.by_category || []).slice(0, 2).map((c) => (
              <Card key={c.category} className="bg-slate-900/60 border-slate-800">
                <CardContent className="pt-5">
                  <p className="text-xs text-slate-400">{CATEGORY_LABELS[c.category] || c.category}</p>
                  <p className="text-2xl font-bold mt-1">{c.count}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-500" />
            <Input
              className="pl-9"
              placeholder="Search by name, tag or uploader"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="media-search-input"
            />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[220px]" data-testid="media-category-select">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {Object.entries(CATEGORY_LABELS).map(([k, label]) => (
                <SelectItem key={k} value={k}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
          </div>
        ) : files.length === 0 ? (
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="py-16 text-center text-slate-400" data-testid="media-empty">
              Nothing here yet — upload your first file.
            </CardContent>
          </Card>
        ) : (
          <>
            <p className="text-xs text-slate-500">
              Showing {files.length} of {total}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {files.map((f) => (
                <Card
                  key={f.id}
                  className="bg-slate-900/60 border-slate-800 overflow-hidden group hover:border-cyan-500/50 transition-colors"
                  data-testid={`media-card-${f.id}`}
                >
                  <button
                    type="button"
                    className="block w-full text-left"
                    onClick={() => setPreview(f)}
                    data-testid={`media-open-${f.id}`}
                  >
                    {isImage(f) ? (
                      <AuthImage
                        src={`${API}/media/${f.id}/raw`}
                        alt={f.title}
                        className="h-36 w-full object-cover"
                        testId={`media-thumb-${f.id}`}
                      />
                    ) : (
                      <div className="h-36 w-full flex items-center justify-center bg-slate-800/60">
                        <FileText className="w-8 h-8 text-slate-500" />
                      </div>
                    )}
                  </button>
                  <CardContent className="pt-3 pb-4 space-y-2">
                    <p className="text-sm text-white truncate" title={f.title}>{f.title}</p>
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="border-slate-700 text-slate-400 text-[10px]">
                        {CATEGORY_LABELS[f.category] || f.category}
                      </Badge>
                      <span className="text-[10px] text-slate-500">{prettyBytes(f.size)}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl" data-testid="media-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-white truncate">{preview?.title}</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-4">
              {(preview.content_type || "").startsWith("image/") ? (
                <AuthImage
                  src={`${API}/media/${preview.id}/raw`}
                  alt={preview.title}
                  className="w-full max-h-[55vh] object-contain rounded-lg bg-slate-950"
                  testId="media-preview-image"
                />
              ) : (
                <div className="py-12 flex flex-col items-center gap-2 text-slate-400">
                  <FileText className="w-10 h-10" />
                  <span className="text-sm">{preview.original_filename}</span>
                </div>
              )}
              <div className="text-xs text-slate-400 space-y-1">
                <p>Uploaded by {preview.uploaded_by_name || "—"}</p>
                <p>{prettyBytes(preview.size)} · {preview.content_type}</p>
                {preview.tags?.length > 0 && <p>Tags: {preview.tags.join(", ")}</p>}
              </div>
              <div className="flex flex-wrap gap-2">
                <a
                  href={`${API}/media/${preview.id}/raw?download=true&auth=${encodeURIComponent(localStorage.getItem("scc_session_token") || "")}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Button size="sm" variant="outline" data-testid="media-download-btn">
                    <Download className="w-3.5 h-3.5 mr-1" /> Download
                  </Button>
                </a>
                <Button size="sm" variant="outline" onClick={() => copyReference(preview)} data-testid="media-copy-ref-btn">
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy reference
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-300 hover:text-red-200"
                  onClick={() => remove(preview.id)}
                  data-testid="media-delete-btn"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
